import React, { useState, useEffect, useRef } from "react";
import BreadCrumb from "Common/BreadCrumb";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { TailSpin } from "react-loader-spinner";
import { X } from "lucide-react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import callApi from "Common/utils/apiCaller";
import JoditEditor from "jodit-react";

const PageForm = () => {
  document.title = "Add Page | SuperSalesIQ";
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [pageData, setPageData] = useState<any>(null);
  const [content, setContent] = useState("");
  const editor = useRef(null);

  useEffect(() => {
    // Check if we're in edit mode (URL contains an ID)
    const searchParams = new URLSearchParams(location.search);
    const pageId = searchParams.get("id");
    
    if (pageId) {
      setIsEdit(true);
      fetchPageData(pageId);
    }
  }, [location]);

  const fetchPageData = async (pageId: string) => {
    setLoading(true);
    try {
      const res = await callApi(`v1/page/${pageId}`, "get");
      if (res && res.data && res.data.data) {
        setPageData(res.data.data);
        setContent(res.data.data.content || "");
        validation.setValues({
          title: res.data.data.title || "",
          slug: res.data.data.slug || "",
          meta_title: res.data.data.meta_title || "",
          meta_description: res.data.data.meta_description || "",
        });
      }
    } catch (error) {
      console.error("Error fetching page data:", error);
      toast("Failed to fetch page data");
    } finally {
      setLoading(false);
    }
  };

  // Form validation
  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      title: "",
      slug: "",
      meta_title: "",
      meta_description: "",
    },
    validationSchema: Yup.object({
      title: Yup.string().required("Please Enter Page Title"),
      slug: Yup.string().required("Please Enter Page Slug"),
      meta_title: Yup.string(),
      meta_description: Yup.string(),
    }),
    onSubmit: async (values) => {
      setLoading(true);
      try {
        if (isEdit) {
          // Update existing page
          const res = await callApi(`v1/page/${pageData._id}/update`, "post", {
            page: {
              title: values.title,
              slug: values.slug,
              content: content,
              meta_title: values.meta_title,
              meta_description: values.meta_description,
            },
          });
          
          if (res && res.data && res.data.status === "Success") {
            toast("Page updated successfully");
            setTimeout(() => {
              navigate("/page");
            }, 1500);
          } else {
            toast("Failed to update page");
          }
        } else {
          // Create new page
          const res = await callApi(`v1/page/create`, "post", {
            page: {
              title: values.title,
              slug: values.slug,
              content: content,
              meta_title: values.meta_title,
              meta_description: values.meta_description,
              is_active: true, // Set active by default
            },
          });
          
          if (res && res.data && res.data.status === "Success") {
            toast("Page created successfully");
            setTimeout(() => {
              navigate("/page");
            }, 1500);
          } else {
            toast("Failed to create page");
          }
        }
      } catch (error) {
        console.error("Error submitting form:", error);
        toast("Something went wrong");
      } finally {
        setLoading(false);
      }
    },
  });

  const config = {
    readonly: false,
    height: 400,
    statusbar: false,
    buttons: [
      "source",
      "|",
      "bold",
      "italic",
      "|",
      "ul",
      "ol",
      "|",
      "font",
      "fontsize",
      "brush",
      "paragraph",
      "|",
      "image",
      "table",
      "link",
      "|",
      "left",
      "center",
      "right",
      "justify",
      "|",
      "undo",
      "redo",
      "|",
      "hr",
      "eraser",
      "fullsize",
    ],
    uploader: {
      insertImageAsBase64URI: true,
    },
    removeButtons: ["brush", "file"],
    showXPathInStatusbar: false,
    showCharsCounter: true,
    showWordsCounter: true,
    toolbarAdaptive: true,
    toolbarSticky: true,
  };

  return (
    <React.Fragment>
      <BreadCrumb title={isEdit ? "Edit Page" : "Add Page"} pageTitle="Pages" />
      <ToastContainer closeButton={false} limit={1} />
      
      <div className="grid grid-cols-12 gap-x-5">
        <div className="col-span-12 card 2xl:col-span-12">
          <div className="card-body">
            <div className="flex items-center">
              <h6 className="text-15 grow">{isEdit ? "Edit Page" : "Add Page"}</h6>
              <div className="shrink-0">
                <Link
                  to="/page"
                  className="text-slate-500 btn bg-slate-200 border-slate-200 hover:text-slate-600 hover:bg-slate-300 hover:border-slate-300 focus:text-slate-600 focus:bg-slate-300 focus:border-slate-300 focus:ring focus:ring-slate-100 active:text-slate-600 active:bg-slate-300 active:border-slate-300 active:ring active:ring-slate-100 dark:bg-zink-600 dark:hover:bg-zink-500 dark:border-zink-600 dark:hover:border-zink-500 dark:text-zink-200 dark:ring-zink-400/20"
                >
                  <X className="inline-block size-4" />{" "}
                  <span className="align-middle">Cancel</span>
                </Link>
              </div>
            </div>
          </div>
          <div className="card-body">
            {loading && !validation.isSubmitting ? (
              <div className="flex items-center justify-center h-60">
                <TailSpin visible={true} height="40" width="40" color="#4fa94d" />
              </div>
            ) : (
              <form
                className="create-form"
                onSubmit={validation.handleSubmit}
              >
                <div className="grid grid-cols-1 gap-5 lg:grid-cols-2 xl:grid-cols-12">
                  <div className="xl:col-span-6">
                    <label
                      htmlFor="title"
                      className="inline-block mb-2 text-base font-medium"
                    >
                      Page Title*
                    </label>
                    <input
                      type="text"
                      id="title"
                      name="title"
                      className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                      placeholder="Enter page title"
                      onChange={validation.handleChange}
                      onBlur={validation.handleBlur}
                      value={validation.values.title}
                    />
                    {validation.touched.title &&
                    validation.errors.title ? (
                      <p className="text-red-500">
                        {validation.errors.title}
                      </p>
                    ) : null}
                  </div>
                  <div className="xl:col-span-6">
                    <label
                      htmlFor="slug"
                      className="inline-block mb-2 text-base font-medium"
                    >
                      Page Slug*
                    </label>
                    <input
                      type="text"
                      id="slug"
                      name="slug"
                      className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                      placeholder="Enter page slug"
                      onChange={validation.handleChange}
                      onBlur={validation.handleBlur}
                      value={validation.values.slug}
                    />
                    {validation.touched.slug &&
                    validation.errors.slug ? (
                      <p className="text-red-500">
                        {validation.errors.slug}
                      </p>
                    ) : null}
                  </div>
                  <div className="xl:col-span-6">
                    <label
                      htmlFor="meta_title"
                      className="inline-block mb-2 text-base font-medium"
                    >
                      Meta Title
                    </label>
                    <input
                      type="text"
                      id="meta_title"
                      name="meta_title"
                      className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                      placeholder="Enter meta title"
                      onChange={validation.handleChange}
                      onBlur={validation.handleBlur}
                      value={validation.values.meta_title}
                    />
                  </div>
                  <div className="xl:col-span-6">
                    <label
                      htmlFor="meta_description"
                      className="inline-block mb-2 text-base font-medium"
                    >
                      Meta Description
                    </label>
                    <input
                      type="text"
                      id="meta_description"
                      name="meta_description"
                      className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                      placeholder="Enter meta description"
                      onChange={validation.handleChange}
                      onBlur={validation.handleBlur}
                      value={validation.values.meta_description}
                    />
                  </div>
                  <div className="xl:col-span-12">
                    <label
                      htmlFor="content"
                      className="inline-block mb-2 text-base font-medium"
                    >
                      Page Content
                    </label>
                    <JoditEditor
                      ref={editor}
                      value={content}
                      config={config as any}
                      onBlur={(newContent) => setContent(newContent)}
                      onChange={(newContent) => {}}
                    />
                  </div>
                  <div className="xl:col-span-12">
                    <div className="flex justify-end gap-2 mt-4">
                      <Link
                        to="/page"
                        className="text-red-500 bg-red-100 btn hover:text-white hover:bg-red-600 focus:text-white focus:bg-red-600 focus:ring focus:ring-red-100 active:text-white active:bg-red-600 active:ring active:ring-red-100 dark:bg-red-500/20 dark:text-red-500 dark:hover:bg-red-500 dark:hover:text-white dark:focus:bg-red-500 dark:focus:text-white dark:active:bg-red-500 dark:active:text-white dark:ring-red-400/20"
                      >
                        Cancel
                      </Link>
                      <button
                        type="submit"
                        disabled={validation.isSubmitting || loading}
                        className="text-white btn bg-custom-500 border-custom-500 hover:text-white hover:bg-custom-600 hover:border-custom-600 focus:text-white focus:bg-custom-600 focus:border-custom-600 focus:ring focus:ring-custom-100 active:text-white active:bg-custom-600 active:border-custom-600 active:ring active:ring-custom-100 dark:ring-custom-400/20"
                      >
                        {validation.isSubmitting || loading ? (
                          <TailSpin
                            visible={true}
                            height="20"
                            width="20"
                            color="#fff"
                            ariaLabel="tail-spin-loading"
                            radius="1"
                            wrapperStyle={{}}
                            wrapperClass=""
                          />
                        ) : isEdit ? (
                          "Update Page"
                        ) : (
                          "Add Page"
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </React.Fragment>
  );
};

export default PageForm;
